export interface IntegrationItem {
  id: string;
  name: string;
  category: IntegrationCategory;
  categoryLabel: string;
  description: string;
  icon: string;
  iconBg: string;
  iconColor: string;
  svgKey?: string;
  status: 'connected' | 'available' | 'native';
  badge?: string;
  features: string[];
  docsUrl?: string;
  popular?: boolean;
  fields?: {
    key: string;
    label: string;
    type: 'text' | 'password' | 'select' | 'url';
    placeholder?: string;
    options?: string[];
    required?: boolean;
    defaultValue?: string;
  }[];
  webhookSupported?: boolean;
  twoWaySync?: boolean;
  last_synced_at?: string | null;
  last_sync_count?: number | null;
  last_sync_leads?: number | null;
  last_sync_contacts?: number | null;
}

export const CRM_LEAD_PROVIDERS = ['hubspot', 'zoho-crm', 'pipedrive', 'freshdesk-crm'] as const;

export const CRM_SYNC_META: Record<
  string,
  { source: string; leadObject: string; contactObject: string }
> = {
  hubspot: {
    source: 'HubSpot',
    leadObject: 'HubSpot Contacts in a Lead stage',
    contactObject: 'HubSpot Contacts',
  },
  'zoho-crm': {
    source: 'Zoho CRM',
    leadObject: 'Zoho Leads',
    contactObject: 'Zoho Contacts',
  },
  pipedrive: {
    source: 'Pipedrive',
    leadObject: 'Pipedrive Leads / Persons',
    contactObject: 'Pipedrive Persons',
  },
  'freshdesk-crm': {
    source: 'Freshsales',
    leadObject: 'Freshsales Leads / Freshdesk Contacts in a Lead lifecycle stage',
    contactObject: 'Freshsales / Freshdesk Contacts',
  },
};

export type IntegrationCategory =
  | 'conversational'
  | 'marketing'
  | 'contact_center'
  | 'crm'
  | 'payments'
  | 'ecommerce'
  | 'forms'
  | 'llm'
  | 'live_chat';

export interface CategoryTab {
  id: 'all' | IntegrationCategory;
  label: string;
  icon: string;
  count?: number;
}

export const INTEGRATION_CATEGORIES: CategoryTab[] = [
  { id: 'all', label: 'All Integrations', icon: 'fas fa-th-large' },
  { id: 'conversational', label: 'Conversational Channels', icon: 'fas fa-comments' },
  { id: 'marketing', label: 'Marketing Automation', icon: 'fas fa-bullhorn' },
  { id: 'contact_center', label: 'Contact Centre', icon: 'fas fa-headset' },
  { id: 'crm', label: 'CRM Systems', icon: 'fas fa-address-card' },
  { id: 'payments', label: 'Payments & Gateways', icon: 'fas fa-credit-card' },
  { id: 'ecommerce', label: 'E-Commerce', icon: 'fas fa-shopping-cart' },
  { id: 'forms', label: 'Forms & Capture', icon: 'fas fa-list-alt' },
  { id: 'llm', label: 'AI & LLM Engines', icon: 'fas fa-brain' },
  { id: 'live_chat', label: 'Live Chat', icon: 'fas fa-comment-dots' },
];

export const ALL_INTEGRATIONS: IntegrationItem[] = [
  /* ================= 1. CONVERSATIONAL CHANNELS ================= */
  {
    id: 'meta-whatsapp',
    name: 'Meta WhatsApp Business',
    category: 'conversational',
    categoryLabel: 'Conversational Channels',
    description: 'Official WhatsApp Business Cloud API for high-volume automated broadcasts, verified templates, and interactive agent chat flows.',
    icon: 'fab fa-whatsapp',
    iconBg: 'rgba(37, 211, 102, 0.12)',
    iconColor: '#25d366',
    svgKey: 'whatsapp',
    status: 'connected',
    badge: 'Verified Cloud API',
    popular: true,
    features: ['Official Cloud API', 'Template Approvals', '2-Way Live Messaging', 'Automated Interactive Chatbots'],
    twoWaySync: true,
    webhookSupported: true,
    fields: [
      { key: 'phone_number_id', label: 'WhatsApp Phone Number ID', type: 'text', placeholder: 'e.g. 1048291049281', required: true },
      { key: 'waba_id', label: 'WhatsApp Business Account ID (WABA)', type: 'text', placeholder: 'e.g. 8920194819284', required: true },
      { key: 'access_token', label: 'Permanent System User Token', type: 'password', placeholder: 'EAAG...', required: true },
      { key: 'webhook_secret', label: 'Webhook Verify Token', type: 'text', placeholder: 'gravitas_webhook_secure_token' },
    ],
  },
  {
    id: 'twilio',
    name: 'Twilio',
    category: 'conversational',
    categoryLabel: 'Conversational Channels',
    description: 'Global SMS, Programmable Voice, IVR call tracking, and WhatsApp messaging gateway across 180+ countries.',
    icon: 'fas fa-phone-volume',
    iconBg: 'rgba(242, 47, 70, 0.12)',
    iconColor: '#f22f46',
    svgKey: 'twilio',
    status: 'available',
    popular: true,
    features: ['Global SMS & MMS', 'Programmable Voice & IVR', 'Real-Time Delivery Logs', 'Number Provisioning'],
    twoWaySync: true,
    webhookSupported: true,
    fields: [
      { key: 'account_sid', label: 'Account SID', type: 'text', placeholder: 'AC...', required: true },
      { key: 'auth_token', label: 'Auth Token', type: 'password', placeholder: 'Your Twilio Auth Token', required: true },
      { key: 'sender_number', label: 'Default From Number / Sender ID', type: 'text', placeholder: '+1 234 567 8900' },
    ],
  },
  {
    id: 'genesys',
    name: 'Genesys Cloud CX',
    category: 'conversational',
    categoryLabel: 'Conversational Channels',
    description: 'Enterprise omnichannel customer orchestration connecting contact routing, voice streams, and digital channels.',
    icon: 'fas fa-network-wired',
    iconBg: 'rgba(255, 79, 0, 0.12)',
    iconColor: '#ff4f00',
    svgKey: 'genesys',
    status: 'available',
    features: ['Omnichannel Routing', 'Workforce Engagement', 'Skill-Based Routing', 'Interaction Analytics'],
    fields: [
      { key: 'region', label: 'Genesys Region', type: 'select', placeholder: 'Select region', options: ['us-east-1 (mypurecloud.com)', 'eu-west-1 (mypurecloud.ie)', 'ap-south-1 (mypurecloud.in)'] },
      { key: 'client_id', label: 'OAuth Client ID', type: 'text', placeholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx', required: true },
      { key: 'client_secret', label: 'OAuth Client Secret', type: 'password', placeholder: 'Secret key', required: true },
    ],
  },
  {
    id: 'meta-suite',
    name: 'Meta (Instagram & Messenger)',
    category: 'conversational',
    categoryLabel: 'Conversational Channels',
    description: 'Engage customers directly from Instagram Direct Messages, Story Mentions, and Facebook Messenger into your unified inbox.',
    icon: 'fab fa-instagram',
    iconBg: 'rgba(225, 48, 108, 0.12)',
    iconColor: '#e1306c',
    svgKey: 'meta',
    status: 'available',
    features: ['Instagram DM Sync', 'Facebook Messenger Hub', 'Story Mention Alerts', 'Unified Lead Inbox'],
    twoWaySync: true,
    webhookSupported: true,
    fields: [
      { key: 'page_id', label: 'Facebook Page ID', type: 'text', placeholder: '1092837492817' },
      { key: 'ig_user_id', label: 'Instagram Business Account ID', type: 'text', placeholder: '178414002938192' },
      { key: 'user_access_token', label: 'Page Access Token', type: 'password', placeholder: 'EAA...', required: true },
    ],
  },

  /* ================= 2. MARKETING AUTOMATION ================= */
  {
    id: 'gravitas-marketing',
    name: 'Gravitas Marketing Studio',
    category: 'marketing',
    categoryLabel: 'Marketing Automation',
    description: 'Native omnichannel visual campaign builder with drip sequences, behavioral event triggers, and analytics.',
    icon: 'fas fa-bolt',
    iconBg: 'rgba(255, 122, 0, 0.12)',
    iconColor: '#ff7a00',
    svgKey: 'gravitas',
    status: 'native',
    badge: 'Native Built-In',
    popular: true,
    features: ['Visual Drip Builder', 'Multi-Channel Sequences', 'Real-Time ROI Analytics', 'Audience Segmentation'],
  },
  {
    id: 'zoho-marketing',
    name: 'Zoho Marketing Automation',
    category: 'marketing',
    categoryLabel: 'Marketing Automation',
    description: 'Sync contacts, lead scores, journeys, and campaign engagement metrics directly from your Zoho Marketing account.',
    icon: 'fas fa-chart-line',
    iconBg: 'rgba(235, 34, 39, 0.12)',
    iconColor: '#eb2227',
    svgKey: 'zoho',
    status: 'available',
    features: ['Contact List Sync', 'Behavioral Scoring', 'Journey Webhooks', 'Campaign Performance Metrics'],
    fields: [
      { key: 'dc_domain', label: 'Zoho Data Center', type: 'select', placeholder: 'Select Data Center', options: ['zoho.com (US)', 'zoho.in (India)', 'zoho.eu (Europe)'] },
      { key: 'client_id', label: 'Zoho Client ID', type: 'text', placeholder: '1000.XXXXX', required: true },
      { key: 'client_secret', label: 'Zoho Client Secret', type: 'password', placeholder: 'Secret', required: true },
    ],
  },
  {
    id: 'dotdigital',
    name: 'Dotdigital',
    category: 'marketing',
    categoryLabel: 'Marketing Automation',
    description: 'Enterprise cross-channel marketing automation with transactional emails, SMS journeys, and customer profiling.',
    icon: 'fas fa-paper-plane',
    iconBg: 'rgba(92, 45, 145, 0.12)',
    iconColor: '#5c2d91',
    svgKey: 'dotdigital',
    status: 'available',
    features: ['Email Campaign Sync', 'Program Journey Triggers', 'Transactional Messaging', 'Contact Field Mapping'],
    fields: [
      { key: 'api_endpoint', label: 'API Endpoint Host', type: 'text', placeholder: 'https://r1-api.dotdigital.com' },
      { key: 'api_user', label: 'API Username', type: 'text', placeholder: 'apiuser@apiconnector.com', required: true },
      { key: 'api_password', label: 'API Password', type: 'password', placeholder: '••••••••', required: true },
    ],
  },

  /* ================= 3. CONTACT CENTRE ================= */
  {
    id: 'gravitas-contact-center',
    name: 'Gravitas Contact Centre',
    category: 'contact_center',
    categoryLabel: 'Contact Centre',
    description: 'Native omnichannel agent console with browser click-to-call, live agent call queue, recording, and real-time supervisor monitoring.',
    icon: 'fas fa-headset',
    iconBg: 'rgba(2, 67, 163, 0.12)',
    iconColor: '#0243a3',
    svgKey: 'gravitas',
    status: 'native',
    badge: 'Native Built-In',
    popular: true,
    features: ['In-Browser WebRTC Calling', 'Live Call Transfer & Recording', 'Queue Management', 'Agent Performance Scorecard'],
  },
  {
    id: 'zendesk',
    name: 'Zendesk Talk & Support',
    category: 'contact_center',
    categoryLabel: 'Contact Centre',
    description: 'Bi-directional sync with Zendesk Support tickets, customer context cards, and Zendesk Talk call records.',
    icon: 'fas fa-life-ring',
    iconBg: 'rgba(3, 54, 61, 0.12)',
    iconColor: '#03363d',
    svgKey: 'zendesk',
    status: 'available',
    features: ['2-Way Ticket Sync', 'Customer 360 Context App', 'Agent Presence Sync', 'Call Recording Integration'],
    fields: [
      { key: 'subdomain', label: 'Zendesk Subdomain', type: 'text', placeholder: 'yourcompany (without .zendesk.com)', required: true },
      { key: 'admin_email', label: 'Admin Email', type: 'text', placeholder: 'admin@company.com', required: true },
      { key: 'api_token', label: 'API Token', type: 'password', placeholder: 'Token from Zendesk Admin', required: true },
    ],
  },
  {
    id: 'salesforce-service',
    name: 'Salesforce Service Cloud',
    category: 'contact_center',
    categoryLabel: 'Contact Centre',
    description: 'Connect enterprise Salesforce Service Cloud cases, contact histories, escalation workflows, and Omni-Channel queues.',
    icon: 'fab fa-salesforce',
    iconBg: 'rgba(0, 161, 224, 0.12)',
    iconColor: '#00a1e0',
    svgKey: 'salesforce',
    status: 'available',
    features: ['Case Bi-Directional Sync', 'Lead & Contact Association', 'Omni-Channel Queue Sync', 'Custom Object Mapping'],
    fields: [
      { key: 'instance_url', label: 'Salesforce Instance URL', type: 'url', placeholder: 'https://yourcompany.my.salesforce.com', required: true },
      { key: 'consumer_key', label: 'Connected App Consumer Key', type: 'text', placeholder: '3MVG9...', required: true },
      { key: 'consumer_secret', label: 'Consumer Secret', type: 'password', placeholder: 'Secret', required: true },
    ],
  },
  {
    id: 'telephony-tata',
    name: 'Telephony Services (Tata / Exotel / Ringover)',
    category: 'contact_center',
    categoryLabel: 'Contact Centre',
    description: 'Connect SIP trunks, cloud telephony providers (Tata Teleservices Smartflo, Exotel, Ringover, Knowlarity) for PRI/DID lines.',
    icon: 'fas fa-phone-alt',
    iconBg: 'rgba(43, 114, 186, 0.12)',
    iconColor: '#2b72ba',
    svgKey: 'telephony',
    status: 'connected',
    badge: 'Active Trunk',
    popular: true,
    features: ['DID & Virtual Numbers', 'SIP Trunk Integration', 'Auto-Dialer Sequences', 'Call Logs & Recording Webhooks'],
    fields: [
      { key: 'provider', label: 'Telephony Provider', type: 'select', placeholder: 'Select Provider', options: ['Tata Teleservices (Smartflo)', 'Ringover Cloud Call', 'Exotel Cloud PBX', 'Generic SIP Trunk'] },
      { key: 'api_key', label: 'API Key / Token', type: 'password', placeholder: 'Provider API Key', required: true },
      { key: 'caller_id', label: 'Outbound Caller ID (CLI)', type: 'text', placeholder: '080-XXXXXXXX' },
    ],
  },

  /* ================= 4. CRM SYSTEMS ================= */
  {
    id: 'gravitas-crm',
    name: 'Gravitas Sales CRM',
    category: 'crm',
    categoryLabel: 'CRM Systems',
    description: 'Full-featured enterprise SaaS CRM with pipeline stages, deal scoring, activity feeds, automated tasks, and RFM intelligence.',
    icon: 'fas fa-cubes',
    iconBg: 'rgba(255, 122, 0, 0.12)',
    iconColor: '#ff7a00',
    svgKey: 'gravitas',
    status: 'native',
    badge: 'Native Core',
    popular: true,
    features: ['Pipeline & Deal Management', 'Lead 360 Customer Profile', 'Automated Lead Assignment', 'AI Customer Intelligence'],
  },
  {
    id: 'hubspot',
    name: 'HubSpot CRM',
    category: 'crm',
    categoryLabel: 'CRM Systems',
    description: 'HubSpot has no Leads module. Sync contacts, capture their stage in Status, and create Gravitas leads when the stage name includes Lead.',
    icon: 'fab fa-hubspot',
    iconBg: 'rgba(255, 122, 89, 0.12)',
    iconColor: '#ff7a59',
    svgKey: 'hubspot',
    status: 'available',
    popular: true,
    webhookSupported: false,
    features: ['Contact Stage Sync', 'Lead Stage → Gravitas Leads', 'Email & Phone Mapping', 'Source: HubSpot'],
    fields: [
      { key: 'access_token', label: 'Private App Access Token', type: 'password', placeholder: 'pat-na1-...', required: true },
      { key: 'portal_id', label: 'HubSpot Hub ID / Portal ID', type: 'text', placeholder: '19827364' },
    ],
  },
  {
    id: 'zoho-crm',
    name: 'Zoho CRM',
    category: 'crm',
    categoryLabel: 'CRM Systems',
    description: 'Sync Zoho leads and contacts using Client ID and Secret. Token scope: ZohoCRM.modules.leads.READ, ZohoCRM.modules.contacts.READ.',
    icon: 'fas fa-briefcase',
    iconBg: 'rgba(235, 34, 39, 0.12)',
    iconColor: '#eb2227',
    svgKey: 'zoho',
    status: 'available',
    webhookSupported: false,
    features: ['Client ID + Secret Sync', 'Lead & Contact Sync', 'Data Center Support', 'Source: Zoho CRM'],
    fields: [
      { key: 'dc_domain', label: 'Zoho Data Center', type: 'select', placeholder: 'Select Data Center', options: ['zoho.com (US)', 'zoho.in (India)', 'zoho.eu (Europe)', 'zoho.com.au (Australia)'], defaultValue: 'zoho.com (US)' },
      { key: 'client_id', label: 'Client ID', type: 'text', placeholder: '1000.XXXXX', required: true },
      { key: 'client_secret', label: 'Client Secret', type: 'password', placeholder: 'Secret', required: true },
    ],
  },
  {
    id: 'pipedrive',
    name: 'Pipedrive',
    category: 'crm',
    categoryLabel: 'CRM Systems',
    description: 'Sync Pipedrive leads and persons into Gravitas. Source on every record is Pipedrive.',
    icon: 'fas fa-funnel-dollar',
    iconBg: 'rgba(19, 178, 93, 0.12)',
    iconColor: '#13b25d',
    svgKey: 'pipedrive',
    status: 'available',
    webhookSupported: false,
    features: ['Lead & Contact Sync', 'Email & Phone Mapping', 'Organization Name', 'Source: Pipedrive'],
    fields: [
      { key: 'api_token', label: 'Pipedrive Personal API Token', type: 'password', placeholder: 'Your API token', required: true },
      { key: 'company_domain', label: 'Company Domain', type: 'text', placeholder: 'yourcompany.pipedrive.com' },
    ],
  },
  {
    id: 'freshdesk-crm',
    name: 'Freshdesk / Freshsales',
    category: 'crm',
    categoryLabel: 'CRM Systems',
    description: 'Sync Freshsales leads and Freshdesk contacts into Gravitas. Freshdesk contacts whose Lifecycle stage contains “lead” are also created as leads.',
    icon: 'fas fa-leaf',
    iconBg: 'rgba(18, 151, 224, 0.12)',
    iconColor: '#1297e0',
    svgKey: 'freshdesk',
    status: 'available',
    webhookSupported: false,
    features: ['Lead & Contact Sync', 'Lead from Lifecycle stage', 'Duplicate Guard', 'Source: Freshsales / Freshdesk'],
    fields: [
      { key: 'domain', label: 'Freshworks Domain URL', type: 'url', placeholder: 'https://yourcompany.myfreshworks.com', required: true },
      { key: 'api_key', label: 'API Key', type: 'password', placeholder: 'Your Freshworks API Key', required: true },
    ],
  },

  /* ================= 5. PAYMENTS & GATEWAYS ================= */
  {
    id: 'stripe',
    name: 'Stripe',
    category: 'payments',
    categoryLabel: 'Payments & Gateways',
    description: 'Accept credit/debit cards, subscriptions, recurring billing, Apple Pay, Google Pay with automated invoice status sync.',
    icon: 'fab fa-stripe-s',
    iconBg: 'rgba(99, 91, 255, 0.12)',
    iconColor: '#635bff',
    svgKey: 'stripe',
    status: 'connected',
    badge: 'Live Gateway',
    popular: true,
    features: ['Credit & Debit Cards', 'Recurring Subscriptions', 'Stripe Webhook Listeners', 'Automated Payment Receipts'],
    fields: [
      { key: 'publishable_key', label: 'Publishable Key', type: 'text', placeholder: 'pk_live_...', required: true },
      { key: 'secret_key', label: 'Secret Key', type: 'password', placeholder: 'sk_live_...', required: true },
      { key: 'webhook_secret', label: 'Webhook Signing Secret', type: 'password', placeholder: 'whsec_...' },
    ],
  },
  {
    id: 'razorpay',
    name: 'Razorpay',
    category: 'payments',
    categoryLabel: 'Payments & Gateways',
    description: 'India’s top payment gateway for UPI, Netbanking, Cards, EMI, PayLater, Payment Links, and Smart Invoicing.',
    icon: 'fas fa-bolt',
    iconBg: 'rgba(12, 108, 242, 0.12)',
    iconColor: '#0c6cf2',
    svgKey: 'razorpay',
    status: 'connected',
    badge: 'Live Gateway',
    popular: true,
    features: ['Instant UPI Auto-Pay', 'Netbanking 50+ Banks', 'Payment Links over WhatsApp', 'Instant Webhook Settlements'],
    fields: [
      { key: 'key_id', label: 'Razorpay Key ID', type: 'text', placeholder: 'rzp_live_...', required: true },
      { key: 'key_secret', label: 'Razorpay Key Secret', type: 'password', placeholder: '••••••••', required: true },
      { key: 'webhook_secret', label: 'Webhook Secret', type: 'password', placeholder: 'Your Webhook Secret' },
    ],
  },
  {
    id: 'worldpay',
    name: 'Worldpay (FIS)',
    category: 'payments',
    categoryLabel: 'Payments & Gateways',
    description: 'Global enterprise merchant payment processing supporting multi-currency cross-border transactions and POS settlements.',
    icon: 'fas fa-globe-americas',
    iconBg: 'rgba(237, 28, 36, 0.12)',
    iconColor: '#ed1c24',
    svgKey: 'worldpay',
    status: 'available',
    features: ['Global Multi-Currency', 'Point-of-Sale (POS) Capture', '3D Secure 2.0 Fraud Shield', 'Batch Settlement Feeds'],
    fields: [
      { key: 'merchant_code', label: 'Merchant Code', type: 'text', placeholder: 'WORLDPAY_MERCHANT_ID', required: true },
      { key: 'service_key', label: 'Service Key', type: 'password', placeholder: 'T_SERVICE_KEY_...', required: true },
      { key: 'client_key', label: 'Client Key', type: 'text', placeholder: 'T_CLIENT_KEY_...' },
    ],
  },
  {
    id: 'ccavenue',
    name: 'CCAvenue',
    category: 'payments',
    categoryLabel: 'Payments & Gateways',
    description: 'South Asia’s leading multi-currency payment gateway with 200+ payment options and real-time transaction verification.',
    icon: 'fas fa-university',
    iconBg: 'rgba(0, 137, 208, 0.12)',
    iconColor: '#0089d0',
    svgKey: 'ccavenue',
    status: 'available',
    features: ['200+ Payment Options', 'Multi-Currency Processing', 'Invoice Payment Integration', 'Dynamic Event Verification'],
    fields: [
      { key: 'merchant_id', label: 'Merchant ID', type: 'text', placeholder: 'CCAvenue Merchant ID', required: true },
      { key: 'access_code', label: 'Access Code', type: 'text', placeholder: 'AV...', required: true },
      { key: 'working_key', label: 'Working Encryption Key', type: 'password', placeholder: '••••••••', required: true },
    ],
  },
  {
    id: 'upi-direct',
    name: 'UPI Direct & Dynamic QR',
    category: 'payments',
    categoryLabel: 'Payments & Gateways',
    description: 'Instant zero-fee customer payments via Dynamic UPI QR codes embedded on invoices, receipts, and WhatsApp chats.',
    icon: 'fas fa-qrcode',
    iconBg: 'rgba(40, 167, 69, 0.12)',
    iconColor: '#28a745',
    svgKey: 'upi',
    status: 'connected',
    badge: 'Instant QR',
    features: ['Dynamic Amount QR', 'Instant Zero-Fee Settlement', 'Payment Link via SMS/WhatsApp', 'Real-Time VPA Verification'],
    fields: [
      { key: 'vpa_id', label: 'Merchant VPA / UPI ID', type: 'text', placeholder: 'merchant@bank', required: true },
      { key: 'payee_name', label: 'Merchant Business Name', type: 'text', placeholder: 'Gravitas Platforms Pvt Ltd', required: true },
    ],
  },

  /* ================= 6. E-COMMERCE ================= */
  {
    id: 'woocommerce',
    name: 'WooCommerce',
    category: 'ecommerce',
    categoryLabel: 'E-Commerce',
    description: 'Full bi-directional synchronization with your WooCommerce WordPress store: products, stock levels, orders, and customer history.',
    icon: 'fas fa-shopping-bag',
    iconBg: 'rgba(150, 88, 138, 0.12)',
    iconColor: '#96588a',
    svgKey: 'woocommerce',
    status: 'connected',
    badge: 'Connected',
    popular: true,
    features: ['Real-Time Order Ingestion', 'Inventory Sync', 'Customer Order History', 'Webhook Instant Trigger'],
    fields: [
      { key: 'store_url', label: 'WooCommerce Store URL', type: 'url', placeholder: 'https://store.yourdomain.com', required: true },
      { key: 'consumer_key', label: 'REST API Consumer Key', type: 'text', placeholder: 'ck_xxxxxxxxxxxxxxxxxxxxxxxx', required: true },
      { key: 'consumer_secret', label: 'REST API Consumer Secret', type: 'password', placeholder: 'cs_xxxxxxxxxxxxxxxxxxxxxxxx', required: true },
    ],
  },
  {
    id: 'shopify',
    name: 'Shopify Store',
    category: 'ecommerce',
    categoryLabel: 'E-Commerce',
    description: 'Connect Shopify storefront orders, customer carts, abandoned cart follow-ups, and customer lifetime value metrics.',
    icon: 'fab fa-shopify',
    iconBg: 'rgba(149, 191, 71, 0.12)',
    iconColor: '#95bf47',
    svgKey: 'shopify',
    status: 'available',
    features: ['Storefront Order Sync', 'Abandoned Cart Automation', 'Customer LTV Tracking', 'Product Catalog Sync'],
    fields: [
      { key: 'shop_domain', label: 'Shopify Store Domain', type: 'text', placeholder: 'yourstore.myshopify.com', required: true },
      { key: 'access_token', label: 'Admin API Access Token', type: 'password', placeholder: 'shpat_...', required: true },
    ],
  },

  /* ================= 7. FORMS & LEAD CAPTURE ================= */
  {
    id: 'gravitas-forms',
    name: 'Gravitas Form Builder & Webhooks',
    category: 'forms',
    categoryLabel: 'Forms & Capture',
    description: 'Native drag-and-drop form builder with shareable links, website embed widgets, and instant lead-routing webhooks.',
    icon: 'fas fa-list-alt',
    iconBg: 'rgba(255, 122, 0, 0.12)',
    iconColor: '#ff7a00',
    svgKey: 'gravitas',
    status: 'native',
    badge: 'Native Built-In',
    popular: true,
    features: ['No-Code Drag & Drop Builder', 'Shareable Public URLs', 'Inline & Pop-up Website Embeds', 'Instant CRM Lead Conversion'],
  },
  {
    id: 'typeform-google-forms',
    name: 'Typeform & Google Forms',
    category: 'forms',
    categoryLabel: 'Forms & Capture',
    description: 'Capture inbound inquiries from Typeform, Jotform, Google Forms, and Webflow forms directly into CRM leads via webhooks.',
    icon: 'fas fa-wpforms',
    iconBg: 'rgba(38, 38, 39, 0.12)',
    iconColor: '#262627',
    svgKey: 'typeform',
    status: 'available',
    features: ['Universal Inbound Webhook', 'Automatic Field Mapping', 'Instant Notification Alerts', 'Spam & Duplication Guard'],
    fields: [
      { key: 'form_name', label: 'External Form Title', type: 'text', placeholder: 'Website Inquiry Form', required: true },
      { key: 'webhook_endpoint', label: 'Generated Inbound Webhook URL', type: 'text', placeholder: 'https://api.crm.gravitas.com/v1/webhook/forms/...', defaultValue: 'https://api.crm.gravitas.com/v1/webhook/forms/ingest-lead' },
    ],
  },

  /* ================= 8. AI & LLM ENGINES ================= */
  {
    id: 'google-gemini',
    name: 'Google Gemini AI',
    category: 'llm',
    categoryLabel: 'AI & LLM Engines',
    description: 'Powers multimodal Vision OCR receipt extraction, Customer 360 RFM intelligence, and Next Best Action analytics.',
    icon: 'fas fa-magic',
    iconBg: 'rgba(66, 133, 244, 0.12)',
    iconColor: '#4285f4',
    svgKey: 'gemini',
    status: 'connected',
    badge: 'Platform Core AI',
    popular: true,
    features: ['Multimodal Receipt Vision OCR', 'RFM Segmentation Engine', 'Next Best Action (Layer 5)', 'Predictive Churn Scoring'],
    fields: [
      { key: 'model_name', label: 'Gemini Model Selection', type: 'select', placeholder: 'Select Model', options: ['gemini-1.5-pro', 'gemini-1.5-flash', 'gemini-2.0-flash-exp'] },
      { key: 'api_key', label: 'Google Cloud / Gemini API Key', type: 'password', placeholder: 'AIzaSy...', required: true },
    ],
  },
  {
    id: 'openai-chatgpt',
    name: 'OpenAI ChatGPT (GPT-4o)',
    category: 'llm',
    categoryLabel: 'AI & LLM Engines',
    description: 'Conversational assistant for automated smart email generation, WhatsApp agent co-pilot, and lead sentiment analysis.',
    icon: 'fas fa-robot',
    iconBg: 'rgba(16, 163, 127, 0.12)',
    iconColor: '#10a37f',
    svgKey: 'openai',
    status: 'available',
    popular: true,
    features: ['GPT-4o Omnimodal Reasoning', 'AI Agent Co-Pilot Auto-Reply', 'Lead Sentiment Detection', 'Smart Email Drafter'],
    fields: [
      { key: 'model', label: 'OpenAI Model', type: 'select', placeholder: 'Select Model', options: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo'] },
      { key: 'api_key', label: 'OpenAI Secret API Key', type: 'password', placeholder: 'sk-proj-...', required: true },
    ],
  },
  {
    id: 'ms-copilot',
    name: 'Microsoft Copilot & Azure OpenAI',
    category: 'llm',
    categoryLabel: 'AI & LLM Engines',
    description: 'Enterprise Microsoft Copilot Studio and Azure OpenAI Service integration for private tenant document intelligence.',
    icon: 'fab fa-windows',
    iconBg: 'rgba(0, 164, 239, 0.12)',
    iconColor: '#00a4ef',
    svgKey: 'copilot',
    status: 'available',
    features: ['Private Azure Cloud Tenant', 'Enterprise Copilot Bot', 'SharePoint Document RAG', 'SSO & Identity Compliance'],
    fields: [
      { key: 'azure_endpoint', label: 'Azure OpenAI Endpoint', type: 'url', placeholder: 'https://your-resource.openai.azure.com/', required: true },
      { key: 'deployment_id', label: 'Model Deployment Name', type: 'text', placeholder: 'gpt-4o-deployment', required: true },
      { key: 'api_key', label: 'Azure API Key', type: 'password', placeholder: 'Key', required: true },
    ],
  },

  /* ================= 9. LIVE CHAT ================= */
  {
    id: 'gravitas-live-chat',
    name: 'Gravitas Live Chat Widget',
    category: 'live_chat',
    categoryLabel: 'Live Chat',
    description: 'Native live website chat widget with real-time visitor tracking, agent auto-assignment, canned responses, and lead capture.',
    icon: 'fas fa-comments',
    iconBg: 'rgba(255, 122, 0, 0.12)',
    iconColor: '#ff7a00',
    svgKey: 'gravitas',
    status: 'native',
    badge: 'Native Built-In',
    popular: true,
    features: ['Real-Time Visitor Presence', 'Customizable Widget Branding', 'Canned Responses & Macros', 'Instant Chat-to-Lead Creation'],
  },
  {
    id: 'intercom',
    name: 'Intercom',
    category: 'live_chat',
    categoryLabel: 'Live Chat',
    description: 'Integrate Intercom messenger conversations, customer user profiles, Fin AI agent triggers, and product tours.',
    icon: 'fas fa-comment-alt',
    iconBg: 'rgba(0, 87, 255, 0.12)',
    iconColor: '#0057ff',
    svgKey: 'intercom',
    status: 'available',
    features: ['Intercom Messenger Sync', 'Fin AI Bot Integration', 'User & Company Attributes Sync', 'In-App Tour Triggering'],
    fields: [
      { key: 'app_id', label: 'Intercom App / Workspace ID', type: 'text', placeholder: 'e.g. xxxxxxxx', required: true },
      { key: 'access_token', label: 'Intercom Access Token', type: 'password', placeholder: 'dG9rZW46...', required: true },
    ],
  },
  {
    id: 'tawkto',
    name: 'Tawk.to',
    category: 'live_chat',
    categoryLabel: 'Live Chat',
    description: 'Free live chat software with visitor monitoring, ticketing integration, and webhook lead synchronization.',
    icon: 'fas fa-headset',
    iconBg: 'rgba(3, 168, 78, 0.12)',
    iconColor: '#03a84e',
    svgKey: 'tawkto',
    status: 'available',
    features: ['Free 24/7 Live Chat', 'Visitor Tracking & Triggers', 'Chat Transcript Webhooks', 'Ticketing Integration'],
    fields: [
      { key: 'property_id', label: 'Tawk.to Property ID', type: 'text', placeholder: 'e.g. 5f0c43...', required: true },
      { key: 'widget_id', label: 'Widget ID', type: 'text', placeholder: 'default / 1e8b...' },
    ],
  },
];
